import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Avatar,
  IconButton,
} from "@mui/material";
import {
  CalendarMonth as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  MoreVert as MoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DateCalendar } from "@mui/x-date-pickers";
import { useFetchBookedAppointment } from "../../services/api/services";
import { BookedAppointmentDetails } from "../../models/BookedAppointmentDetails";
import { AppointmentStatus } from "../../models/enums/AppointmentStatus";

const DoctorBookingPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [tabValue, setTabValue] = useState(0);
  const [fetchBookedAppointmentState, fetchBookedAppointment] =
    useFetchBookedAppointment();

  const appointments: BookedAppointmentDetails[] =
    fetchBookedAppointmentState.data ?? [];

  useEffect(() => {
    fetchBookedAppointment();
  }, []);

  // Format date for filtering
  const formatDateForFilter = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // Handle tab change
  const handleTabChange = (event: any, newValue: any) => {
    setTabValue(newValue);
  };

  // Handle date change
  const handleDateChange = (newDate: any) => {
    setSelectedDate(newDate);
  };

  // Get status color and icon
  const getStatusStyle = (status: number | undefined) => {
    switch (status) {
      case AppointmentStatus.PENDING:
        return {
          color: "#f57c00",
          bgcolor: "#fff3e0",
          icon: <AccessTimeIcon fontSize="small" />,
        };
      case AppointmentStatus.APPROVED:
        return {
          color: "#388e3c",
          bgcolor: "#e8f5e9",
          icon: <CheckIcon fontSize="small" />,
        };
      case AppointmentStatus.CANCELED:
        return {
          color: "#d32f2f",
          bgcolor: "#ffebee",
          icon: <CloseIcon fontSize="small" />,
        };
      default:
        return {
          color: "#757575",
          bgcolor: "#f5f5f5",
          icon: <AccessTimeIcon fontSize="small" />,
        };
    }
  };

  // Filter appointments based on selected date, tab value
  const filteredAppointments = appointments.filter((appointment) => {
    // Filter by date
    const appointmentDateMatch =
      formatDateForFilter(selectedDate) === appointment.bookedDate;

    // Filter by tab (status)
    let statusMatch = true;
    if (tabValue === 1)
      statusMatch = appointment.status === AppointmentStatus.APPROVED;
    else if (tabValue === 2)
      statusMatch = appointment.status === AppointmentStatus.PENDING;
    else if (tabValue === 3)
      statusMatch = appointment.status === AppointmentStatus.CANCELED;

    return appointmentDateMatch && statusMatch;
  });

  // Get initials from name
  const getInitials = (name: any) => {
    return name
      .split(" ")
      .map((part: any) => part[0])
      .join("")
      .toUpperCase();
  };

  // Format time for display
  const formatTimeForDisplay = (time: any) => {
    return time;
  };

  return (
    <>
      <Container sx={{ mt: 4, mb: 4 }} style={{ maxWidth: "100%" }}>
        <Grid container spacing={3}>
          {/* Calendar and Stats Section */}
          <Grid item xs={12} md={3}>
            <Paper
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f5f7fa",
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Calendar
                </Typography>
              </Box>
              <Box sx={{ p: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateCalendar
                    value={selectedDate}
                    onChange={handleDateChange}
                    sx={{ width: "100%" }}
                  />
                </LocalizationProvider>
              </Box>
            </Paper>

            {/* Summary Stats */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    borderLeft: "4px solid #1976d2",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Today's Appointments
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {
                      appointments.filter(
                        (a) => a.bookedDate === formatDateForFilter(new Date())
                      ).length
                    }
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderLeft: "4px solid #388e3c",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Confirmed
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="#388e3c">
                    {
                      appointments.filter(
                        (a) =>
                          a.status === AppointmentStatus.APPROVED &&
                          a.bookedDate === formatDateForFilter(selectedDate)
                      ).length
                    }
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderLeft: "4px solid #f57c00",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Pending
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="#f57c00">
                    {
                      appointments.filter(
                        (a) =>
                          a.status === AppointmentStatus.PENDING &&
                          a.bookedDate === formatDateForFilter(selectedDate)
                      ).length
                    }
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Appointment List Section */}
          <Grid item xs={12} md={9}>
            <Paper
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
              }}
            >
              {/* Date display and navigation */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f5f7fa",
                  borderBottom: "1px solid #e0e0e0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CalendarIcon sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" fontWeight={600}>
                    {selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </Typography>
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(newDate.getDate() - 1);
                      setSelectedDate(newDate);
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newDate = new Date(selectedDate);
                      newDate.setDate(newDate.getDate() + 1);
                      setSelectedDate(newDate);
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Filter tabs */}
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 500,
                    },
                  }}
                >
                  <Tab label="All" />
                  <Tab label="Approved" />
                  <Tab label="Pending" />
                  <Tab label="Cancelled" />
                </Tabs>
              </Box>

              {/* Appointment Cards */}
              <Box
                sx={{ p: 2, height: "calc(100vh - 320px)", overflow: "auto" }}
              >
                {filteredAppointments.length > 0 ? (
                  filteredAppointments.map((appointment, index) => {
                    const statusStyle = getStatusStyle(appointment.status);
                    return (
                      <Card
                        key={index}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          border: "1px solid #f0f0f0",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Grid container spacing={2}>
                            {/* Time column */}
                            <Grid
                              item
                              xs={12}
                              sm={3}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: { xs: "flex-start", sm: "center" },
                                justifyContent: "flex-start",
                                borderRight: {
                                  xs: "none",
                                  sm: "1px solid #f0f0f0",
                                },
                              }}
                            >
                              <Typography
                                variant="h5"
                                fontWeight={600}
                                color="primary.main"
                              >
                                {formatTimeForDisplay(appointment.bookedTime)}
                              </Typography>
                            </Grid>

                            {/* Patient details */}
                            <Grid item xs={12} sm={7}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    mr: 2,
                                    bgcolor: `hsl(${index * 40}, 70%, 80%)`,
                                    color: `hsl(${index * 40}, 70%, 30%)`,
                                  }}
                                >
                                  {getInitials(appointment.patientName)}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    {appointment.patientName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    Date: {appointment.bookedDate}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography variant="body2" sx={{ ml: 6 }}>
                                <strong>Reason:</strong>{" "}
                                {/* {appointment.patientName} */}
                              </Typography>
                              <Typography variant="body2" sx={{ ml: 6 }}>
                                <strong>Contact:</strong>{" "}
                                {appointment.phoneNumber}
                              </Typography>
                            </Grid>

                            {/* Status and actions */}
                            <Grid
                              item
                              xs={12}
                              sm={2}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: { xs: "flex-start", sm: "center" },
                                justifyContent: "center",
                              }}
                            >
                              <Chip
                                icon={statusStyle.icon}
                                label={appointment.status}
                                sx={{
                                  backgroundColor: statusStyle.bgcolor,
                                  color: statusStyle.color,
                                  fontWeight: 500,
                                  mb: 2,
                                }}
                              />

                              <Box sx={{ display: "flex", gap: 1 }}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  sx={{ bgcolor: "#e3f2fd" }}
                                >
                                  <PhoneIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  sx={{ color: "#757575", bgcolor: "#f5f5f5" }}
                                >
                                  <MoreIcon />
                                </IconButton>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "text.secondary",
                      p: 4,
                    }}
                  >
                    <CalendarIcon
                      sx={{ fontSize: 60, color: "#e0e0e0", mb: 2 }}
                    />
                    <Typography variant="h6" gutterBottom>
                      No Appointments Found
                    </Typography>
                    <Typography variant="body2" align="center">
                      There are no appointments scheduled for this date with the
                      selected filters.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default DoctorBookingPage;
